import { ActionIcon, Anchor, Badge, Group, NumberFormatter, Stack, Text } from '@mantine/core';
import { IconChevronDown, IconChevronRight } from '@tabler/icons-react';
import { createColumnHelper, type Row } from '@tanstack/react-table';
import {
  ExpandedRow,
  getTypeCellLabel,
  SortableHeader,
} from 'app/Private/Clients/Detail/BalanceTab/BalanceTable/component';
import dayjs from 'dayjs';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { i18nKeys } from 'locales/index';
import { useTranslation } from 'locales/logic';
import { useRef } from 'react';
import { isNullish } from 'remeda';
import { getCurrencySymbolFromCurrencyCode } from 'shared/utils/currency';
import { getBalanceColor } from '../../balance-utils';
import { NumberFormatterTable } from '../component/NumberFormatterTable';
import type { SortDirection, SortField, TableRow } from '../types/table-row-types';

export type { SortDirection, SortField, TableRow };
export { BalanceSubRows } from '../component/BalanceSubRows';
export { NumberFormatterTable } from '../component/NumberFormatterTable';

const columnHelper = createColumnHelper<TableRow>();

type UseBalanceTableColumnsProps = {
  openModal: (id: number, isCreditNote: boolean) => void;
  total: number;
  isForceUnfoldActive?: boolean;
  toggleForceUnfold?: () => void;
  sortField?: SortField;
  sortDirection?: SortDirection;
  onSort?: (field: 'issueDate' | 'dueDate') => void;
};

export const useBalanceTableColumns = ({
  openModal,
  total,
  isForceUnfoldActive,
  toggleForceUnfold,
  sortField,
  sortDirection,
  onSort,
}: UseBalanceTableColumnsProps) => {
  const { t } = useTranslation();
  const company = useCompanyConfiguration();

  const lastToggledRowIdRef = useRef<string | null>(null);

  const handleRowToggle = (rowId: string) => {
    lastToggledRowIdRef.current = rowId;
  };

  const Icon = isForceUnfoldActive ? IconChevronDown : IconChevronRight;

  return [
    {
      id: 'collapse',
      header: () =>
        toggleForceUnfold ? (
          <ActionIcon variant="transparent" onClick={toggleForceUnfold}>
            <Icon size={18} />
          </ActionIcon>
        ) : (
          <IconChevronDown size={18} />
        ),
      cell: ({ row }: { row: Row<TableRow> }) =>
        row.getCanExpand() ? <ExpandedRow row={row} onToggle={handleRowToggle} /> : null,
    },
    columnHelper.accessor('reference', {
      header: t(i18nKeys.FORM.REFERENCE),
      cell: info => {
        const row = info.row.original;
        const value = info.getValue();
        const isCreditNote = row.type === 'credit_note';

        const referenceText =
          row.id != null ? (
            <Anchor
              c="purple.5"
              underline="always"
              onClick={() => openModal(Number(row.id), isCreditNote)}
            >
              <Text size="sm">{value}</Text>
            </Anchor>
          ) : (
            <Text size="sm">{value}</Text>
          );

        return (
          <Stack gap={4}>
            {referenceText}
            <Group gap={4}>
              {getTypeCellLabel(row.type)}
              {!isNullish(row.status) && (
                <Badge px={6} bdrs={6} variant="light" color="dimmed" size="sm">
                  {row.status}
                </Badge>
              )}
            </Group>
          </Stack>
        );
      },
    }),
    columnHelper.accessor('issueDate', {
      header: () => (
        <SortableHeader
          label={t(i18nKeys.ISSUE_DATE_SHORT)}
          field="issueDate"
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={onSort}
        />
      ),
      cell: info => <Text size="sm">{dayjs(info.getValue()).format('L')}</Text>,
    }),
    columnHelper.accessor('dueDate', {
      header: () => (
        <SortableHeader
          label={t(i18nKeys.DUE_DATE_SHORT)}
          field="dueDate"
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={onSort}
        />
      ),
      cell: info => {
        const value = info.getValue();
        if (value) return <Text size="sm">{dayjs(value).format('L')}</Text>;
        return null;
      },
    }),
    columnHelper.accessor('debit', {
      id: 'debit',
      header: t(i18nKeys.DEBIT),
      cell: info => {
        const value = info.getValue();
        if (value === 0) return null;
        return <NumberFormatterTable value={value} color="red.5" />;
      },
    }),
    columnHelper.accessor('credit', {
      header: t(i18nKeys.CREDIT),
      cell: info => <NumberFormatterTable value={info.getValue()} color="validation.6" />,
    }),
    columnHelper.accessor('balance', {
      header: t(i18nKeys.BALANCE),
      cell: info => {
        const value = info.getValue();
        return <NumberFormatterTable value={value} color={getBalanceColor(value)} />;
      },
      footer: () => (
        <Text fw="bold" c={getBalanceColor(total)}>
          <NumberFormatter
            thousandSeparator=" "
            value={total}
            decimalScale={2}
            suffix={` ${getCurrencySymbolFromCurrencyCode(company.currency)}`}
          />
        </Text>
      ),
    }),
  ];
};
